import { useCallback, useEffect, useState } from 'react'
import type { BacklogConnectionResponse } from '@ai-meeting/shared'
import {
  completeBacklogOauth,
  disconnectBacklog,
  fetchBacklogConnection,
  startBacklogOauth,
} from '@/services/backlog'
import { rememberReturnPath } from '@/utils/backlogReturnPath'

export type BacklogConnectionPhase = 'loading' | 'idle' | 'saving'

export interface BacklogConnectionState {
  connection: BacklogConnectionResponse | null
  phase: BacklogConnectionPhase
  error: string | null
  /** The one thing callers gate on: whether a sync would be allowed to run. */
  connected: boolean
  /**
   * Where to send the browser to approve the application, or null when that could not be asked.
   *
   * Returns the URL rather than navigating, so the navigation stays in the component that the
   * reader pressed — the same split `UpcomingCalendar` already makes for the calendar grants.
   */
  startSso: () => Promise<string | null>
  /**
   * Hand back what Backlog put on the redirect.
   *
   * @returns Whether the grant was accepted — the callback screen uses it to
   *   decide whether to send the reader back where they started.
   */
  completeSso: (code: string, state: string) => Promise<boolean>
  /**
   * Give up the stored grant. Resolves false and reports through `error` when
   * the server refused, so the card can stay put and say why.
   */
  disconnect: () => Promise<boolean>
}

/**
 * The reader's own Backlog grant.
 *
 * Fetched rather than assumed whenever the screen opens, because the question
 * is not "was a grant accepted once" but "does it work now" — an application
 * revoked in Backlog leaves the stored row untouched.
 *
 * Lives in a hook rather than inside the panel because the sync depends on the
 * same answer: every Backlog call now carries the caller's key, so a check run
 * without one would only ever come back 403.
 *
 * Forgetting a grant on purpose is possible now, and the settings screen is why: it names the
 * account the application acts as, and a screen that says "connected as Thao" without a way to
 * stop being connected as Thao sends the reader off to a Backlog admin page to find out.
 *
 * @param enabled Skip everything while Backlog is unconfigured or the meeting
 *   is not the reader's to sync.
 */
export function useBacklogConnection({ enabled = true }: { enabled?: boolean } = {}):
  BacklogConnectionState {
  const [connection, setConnection] = useState<BacklogConnectionResponse | null>(null)
  const [phase, setPhase] = useState<BacklogConnectionPhase>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    setPhase('loading')
    fetchBacklogConnection()
      .then((result) => {
        if (!cancelled) setConnection(result)
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught.message)
      })
      .finally(() => {
        if (!cancelled) setPhase('idle')
      })
    return () => {
      cancelled = true
    }
  }, [enabled])

  const startSso = useCallback(async () => {
    setPhase('saving')
    setError(null)
    try {
      const url = (await startBacklogOauth()).authorizationUrl
      // Written down here rather than by each caller: the approval comes back
      // to one registered address whichever screen began it, so every screen
      // that starts the trip needs this and none of them can be left to
      // remember it.
      rememberReturnPath(`${window.location.pathname}${window.location.search}`)
      return url
    } catch (caught) {
      setError((caught as Error).message)
      // Only cleared on the failing path: on the other one this tab is about to leave, and
      // flashing the button back to enabled on the way out says the press did not take.
      setPhase('idle')
      return null
    }
  }, [])

  const completeSso = useCallback(async (code: string, state: string) => {
    setPhase('saving')
    setError(null)
    try {
      setConnection(await completeBacklogOauth(code, state))
      return true
    } catch (caught) {
      setError((caught as Error).message)
      // Reported rather than thrown: a refused grant leaves the reader on this
      // screen, which is where the error and the button to try again are.
      return false
    } finally {
      setPhase('idle')
    }
  }, [])

  const disconnect = useCallback(async () => {
    setPhase('saving')
    setError(null)
    try {
      await disconnectBacklog()
      // Set from here rather than re-fetched: the server has just been told,
      // and a round trip to be told the same thing would leave the card
      // showing a connected account for as long as it took.
      setConnection({ status: 'CONNECTION_REQUIRED', space: null, account: null, method: null, connectedAt: null })
      return true
    } catch (caught) {
      setError((caught as Error).message)
      return false
    } finally {
      setPhase('idle')
    }
  }, [])

  return {
    connection,
    phase,
    error,
    connected: connection?.status === 'ACTIVE',
    startSso,
    completeSso,
    disconnect,
  }
}

/**
 * The two states the reader can act on, and the only two that show the button.
 *
 * Here rather than beside either rendering of it: the settings card draws its
 * own action row because the card already carries the title and the status,
 * and nesting `BacklogConnectionPanel` inside it printed both twice. Two
 * copies of this decision would disagree about whether there is anything to
 * press the first time a status is added.
 */
export function needsBacklogGrant(status: string | undefined): boolean {
  return status === 'CONNECTION_REQUIRED' || status === 'REAUTH_REQUIRED'
}
